/**
 * Tiny audio manager for the cursive tracing experience.
 *
 * Assets:
 *   /audio/bgm.mp3    "Carefree" by Kevin MacLeod (incompetech.com) — CC-BY 4.0.
 *   /audio/cheer.mp3  Kid-crowd cheering + applause (Mixkit royalty-free).
 *
 * Provides:
 *  - `startMusic()` / `stopMusic()`   → loop the playful background track.
 *  - `playCheer()`                    → one-shot cheer + applause when confetti fires.
 *  - `isMuted()` / `setMuted(bool)`   → global mute (persisted to localStorage).
 *
 * Notes:
 *  - Uses plain HTMLAudioElement (no Web Audio API) so it works everywhere and
 *    stays tiny. Files live in /audio and are precached by the service worker.
 *  - Browsers require a user gesture before audio plays. `startMusic()` swallows
 *    the resulting `NotAllowedError` — the next click (or the mute toggle) will
 *    kick playback in.
 */

const BGM_URL = "/audio/bgm.mp3";
const CHEER_URL = "/audio/cheer.mp3";
const MUSIC_VOLUME = 0.25;   // sits comfortably under narration / TTS
const CHEER_VOLUME = 0.7;
const MUTE_KEY = "zl_bgm_muted";

let bgm = null;         // Singleton loop element
let bgmActive = false;  // Callers that requested music (ref-count style)

const isBrowser = () => typeof window !== "undefined" && !!window.Audio;

function readMuted() {
  if (!isBrowser()) return false;
  try { return localStorage.getItem(MUTE_KEY) === "1"; }
  catch { return false; }
}

function ensureBgm() {
  if (bgm || !isBrowser()) return bgm;
  bgm = new Audio(BGM_URL);
  bgm.loop = true;
  bgm.preload = "auto";
  bgm.volume = MUSIC_VOLUME;
  return bgm;
}

export function isMuted() {
  return readMuted();
}

export function setMuted(next) {
  if (!isBrowser()) return;
  try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch {}
  if (next) {
    if (bgm) { bgm.pause(); }
  } else if (bgmActive) {
    ensureBgm();
    bgm.play().catch(() => {});
  }
  // Notify listeners (mute-toggle icons) so their UI state matches.
  window.dispatchEvent(new CustomEvent("zl-audio-mute", { detail: { muted: !!next } }));
}

export function startMusic() {
  bgmActive = true;
  if (!isBrowser() || readMuted()) return;
  ensureBgm();
  // .play() returns a promise; swallow autoplay-block silently.
  bgm.play().catch(() => {});
}

export function stopMusic() {
  bgmActive = false;
  if (bgm) {
    bgm.pause();
    try { bgm.currentTime = 0; } catch {}
  }
}

export function playCheer() {
  if (!isBrowser() || readMuted()) return;
  // Build a fresh Audio each time so overlapping cheers don't get cut off.
  const a = new Audio(CHEER_URL);
  a.volume = CHEER_VOLUME;
  a.play().catch(() => {});
}
